import { merge, of, Subject } from 'rxjs';
import {
	filter, mergeMap, switchMap, takeUntil,
} from 'rxjs/operators';

import { Container, Service } from '../../../../symphony';

import { SlActions } from './sl-actions';
import { SlDiscussContext } from '../../global-constants';
import { SlActionsController } from './sl-actions.controller';
import { IAction, ActionType, RequestType } from '../../global-interface';

@Service()
export class SlActionsService {
	private actions$ = Container.take(SlDiscussContext, SlActions).actions$;

	private controller = Container.take(SlDiscussContext, SlActionsController);

	private cancelableActionsMap = {};

	private parallelActionsMap = {
		// Mount events
		[ActionType.OnDiscussQuestionMount]:
            this.controller.onDiscussQuestionMountHandler,
		[ActionType.OnDiscussQuestionUnMount]:
            this.controller.onDiscussQuestionUnMountHandler,
		[ActionType.OnDiscussRepliesMount]:
            this.controller.onDiscussRepliesMountHandler,
		[ActionType.OnDiscussRepliesUnMount]:
            this.controller.onDiscussRepliesUnMountHandler,
		[ActionType.OnHotTodayMount]: this.controller.onHotTodayMountHandler,
		[ActionType.OnDiscussQuestionsListMount]:
            this.controller.onDiscussQuestionsListMountHandler,
		[ActionType.OnDiscussQuestionsListPageMount]:
            this.controller.onDiscussQuestionsListPageMountHandler,
		[ActionType.OnCreateEditQuestionMount]:
            this.controller.onCreateEditQuestionMountHandler,
		[ActionType.OnCreateEditQuestionUnMount]:
            this.controller.onCreateEditQuestionUnMountHandler,
		[ActionType.OnPostQuestionModalMount]:
            this.controller.onPostQuestionModalMountHandler,

		// Click events
		[ActionType.OnPositiveReactionClick]:
            this.controller.onPositiveReactionClickHandler,
		[ActionType.OnReplySaveClick]: this.controller.onReplySaveClickHandler,
		[ActionType.OnAnswerButtonClick]:
            this.controller.onAnswerButtonClickHandler,
		[ActionType.OnCreateReplyCancelClick]:
            this.controller.onCreateReplyCancelClickHandler,
		[ActionType.OnCloseSaveReplySuccessMessage]:
            this.controller.onCloseSaveReplySuccessMessageHandler,
		[ActionType.OnBestAnswerButtonClick]:
            this.controller.onBestAnswerButtonClickHandler,
		[ActionType.OnProfileButtonClick]:
            this.controller.onProfileButtonClickHandler,
		[ActionType.OnQuestionClick]: this.controller.onQuestionClickHandler,
		[ActionType.OnTopicClick]: this.controller.onTopicClickHandler,
		[ActionType.OnTagClick]: this.controller.onTagClickHandler,
		[ActionType.OnSearchClick]: this.controller.onSearchClickHandler,
		[ActionType.OnAskAQuestionClick]:
            this.controller.onAskAQuestionClickHandler,
		[ActionType.OnPaginationClick]:
            this.controller.onPaginationClickHandler,
		[ActionType.OnEmptyQuestionCTAClick]:
            this.controller.onEmptyQuestionCTAClickHandler,
		[ActionType.OnQuestionSubmit]: this.controller.onQuestionSubmitHandler,
		[ActionType.OnQuestionPostCancelClick]:
            this.controller.onQuestionPostCancelClickHandler,
		[ActionType.OnQuestionDeleteClick]:
            this.controller.onQuestionDeleteClickHandler,
		[ActionType.OnPostQuestionModalCloseClick]:
            this.controller.onPostQuestionModalCloseClickHandler,
		[ActionType.OnSeeAllCoursesClick]:
            this.controller.onSeeAllCoursesClickHandler,
		[ActionType.OnRecommendedCourseClick]:
            this.controller.onRecommendedCourseClickHandler,
		[ActionType.OnQuestionDeleteModalCloseClick]:
            this.controller.onQuestionDeleteModalCloseClickHandler,
		[ActionType.OnQuestionDeleteModalDeleteClick]:
            this.controller.onQuestionDeleteModalDeleteClickHandler,
		[ActionType.OnReplyDeleteModalCloseClick]:
            this.controller.onReplyDeleteModalCloseClickHandler,
		[ActionType.OnReplyDeleteModalDeleteClick]:
            this.controller.onReplyDeleteModalDeleteClickHandler,
		[ActionType.OnQuestionEditClick]:
            this.controller.onQuestionEditClickHandler,
		[ActionType.OnJumpLinkClick]: this.controller.onJumpLinkClickHandler,
		[ActionType.OnReplyEditButtonClick]:
            this.controller.onReplyEditButtonClickHandler,
		[ActionType.OnDeleteReplyClick]:
            this.controller.onDeleteReplyClickHandler,

		// Input events
		[ActionType.OnPostTagInputChange]:
            this.controller.onPostTagInputChangeHandler,

		// Business events
		[ActionType.UpdatePositiveReaction]:
            this.controller.updatePositiveReactionHandler,
		[ActionType.GetDiscussQuestion]:
            this.controller.getDiscussQuestionHandler,
		[ActionType.GetDiscussReplies]:
            this.controller.getDiscussRepliesHandler,
		[ActionType.GetQuestionsList]: this.controller.getQuestionsListHandler,
		[ActionType.CreateReply]: this.controller.createReplyHandler,
		[ActionType.EditReply]: this.controller.EditReplyHandler,
		[ActionType.GetHotTodayTopics]:
            this.controller.getHotTodayTopicsHandler,
		[ActionType.ToggleAcceptedAnswer]:
            this.controller.toggleAcceptedAnswerHandler,
		[ActionType.TriggerReplyFormVisibility]:
            this.controller.triggerReplyFormVisibilityHandler,
		[ActionType.TriggerCreateReplySuccessMessageVisibility]:
            this.controller.triggerCreateReplySuccessMessageVisibilityHandler,
		[ActionType.TriggerEditReplySuccessMessageVisibility]:
            this.controller.triggerEditReplySuccessMessageVisibilityHandler,
		[ActionType.TriggerNotificationVisibility]:
            this.controller.triggerNotificationVisibilityHandler,
		[ActionType.QuestionFilterChange]:
            this.controller.questionFilterChangeHandler,
		[ActionType.PostQuestion]: this.controller.postQuestionHandler,
		[ActionType.EditQuestion]: this.controller.editQuestionHandler,
		[ActionType.DeleteQuestion]: this.controller.deleteQuestionHandler,
		[ActionType.GetTags]: this.controller.getTagsHandler,
		[ActionType.DeleteReply]: this.controller.deleteReplyHandler,

		// Track Actions
		[ActionType.Track]: this.controller.onTrackActionHandler,
	};

	constructor() {
		const unsubscriber: Subject<void> = Container.take(
			SlDiscussContext,
			'unsubscriber',
		);
		const cancelable = this.actions$.pipe(
			filter((a) => !!a && a.requestType === RequestType.Cancelable),
			switchMap((action: IAction) => {
				const handler = this.cancelableActionsMap[action.type];
				return handler ? handler(action.data) : of(action);
			}),
		);

		const parallel = this.actions$.pipe(
			filter((a) => !!a && a.requestType === RequestType.Parallel),
			mergeMap((action: IAction) => {
				const handler = this.parallelActionsMap[action.type];
				return handler ? handler(action.data) : of(action);
			}),
		);

		merge(cancelable, parallel)
			.pipe(
				filter((a) => !!a),
				takeUntil(unsubscriber),
			)
			.subscribe();
	}
}
